import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { GenerateReportWizard } from "../Reports/GenerateReportWizard";
import {
  MarketDataAssetClassRequiredModal,
  MarketDataQuoteVariantModal,
} from "../Reports/InstrumentResolveModals";
import { useGenerateReportFlow } from "../Reports/useGenerateReportFlow";
import type { Report } from "../../../types/reports";
import type { WorkspaceCreateContext } from "../../../types/workspace";
import { WorkspaceWizardDialog, WORKSPACE_WIZARD_DIALOG_SIZE_CLASS } from "./WorkspaceWizardDialog";

export type WorkspaceGenerateReportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, generate attaches the report onto the canvas under `parentNodeId`. */
  workspaceContext?: WorkspaceCreateContext;
  lockBrokerAccountCode: string;
  prefillStrategyId?: string | null;
  /** Canvas strategy card title when creating a report under a strategy node. */
  prefillStrategyName?: string | null;
  onReportGenerated: (result: { report: Report; accepted: boolean }) => void;
  /** Instant canvas placeholder while generate is in flight. Returns live placement for the API. */
  onGenerateStarted?: (preview: {
    title: string;
    strategyEntityId?: string | null;
  }) => void | { parentNodeId: string; position: { x: number; y: number } };
  onGenerateFailed?: () => void;
};

/**
 * Create-report host — centered dialog with the stepped generate wizard.
 * Used from the canvas (with workspace placement) and the hub (account-only).
 */
export function WorkspaceGenerateReportDialog({
  open,
  onOpenChange,
  workspaceContext,
  lockBrokerAccountCode,
  prefillStrategyId,
  prefillStrategyName,
  onReportGenerated,
  onGenerateStarted,
  onGenerateFailed,
}: WorkspaceGenerateReportDialogProps): React.ReactElement {
  const strategyPrefill = Boolean(prefillStrategyId?.trim());
  const omitStrategyStep = strategyPrefill;
  const canvasAttach = Boolean(workspaceContext?.workspaceId?.trim());
  const [barCoverageOpen, setBarCoverageOpen] = React.useState(false);
  // Keep the flow mounted after Generate while the dialog chrome closes immediately.
  const [hideWhileGenerating, setHideWhileGenerating] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setBarCoverageOpen(false);
      setHideWhileGenerating(false);
    }
  }, [open]);

  const flow = useGenerateReportFlow({
    embedMode: true,
    hasInflightReport: false,
    lockBrokerAccountCode,
    prefillStrategyId,
    prefillStrategyName,
    workspaceContext,
    onEmbedGenerateStart: (preview) => onGenerateStarted?.(preview),
    onEmbedGenerateFailed: (options) => {
      onGenerateFailed?.();
      if (options?.keepDialogOpen) {
        setHideWhileGenerating(false);
      } else {
        onOpenChange(false);
      }
    },
    onReportGenerated: (result) => {
      setHideWhileGenerating(true);
      onReportGenerated(result);
      onOpenChange(false);
    },
    onEmbedCancel: () => onOpenChange(false),
  });

  const { editStrategyModal, instrumentResolveModals } = flow;
  const dialogOpen = open && !hideWhileGenerating;

  const dialogDescription = strategyPrefill
    ? canvasAttach
      ? "Create a report under this strategy. It will appear on the canvas when generation starts."
      : "Create a report under this strategy for the active account."
    : canvasAttach
      ? "Create a report linked to this workspace. Choose a strategy to insert it in the chain, or skip to hang the report off the account."
      : "Create a report for the active account. Optionally attach it under a strategy.";

  const strategyStepDescription = omitStrategyStep
    ? undefined
    : canvasAttach
      ? "Use an existing strategy to add it to this workspace and attach the report under it. Choose no strategy to hang the report directly off the account."
      : "Optionally attach this report under an existing strategy, or continue without a strategy.";

  return (
    <>
      <WorkspaceWizardDialog
        open={dialogOpen}
        onOpenChange={onOpenChange}
        title="Generate report"
        description={dialogDescription}
        hideIntro={barCoverageOpen}
        animateHeight
        pinFooterSlot
        // Narrow enough for two fields per row; height follows content (no forced portrait).
        className={WORKSPACE_WIZARD_DIALOG_SIZE_CLASS}
        bodyClassName="flex flex-col p-0"
      >
        <GenerateReportWizard
          {...flow.wizardProps}
          layout="dialog"
          resetToken={open}
          lockBrokerAccount
          omitStrategyStep={omitStrategyStep}
          strategyStepDescription={strategyStepDescription}
          onBarCoverageOpenChange={setBarCoverageOpen}
          subtleText="var(--muted-foreground)"
          textColor="var(--foreground)"
          borderColor="var(--border)"
          onCancel={() => onOpenChange(false)}
        />
      </WorkspaceWizardDialog>

      {instrumentResolveModals.assetClassPayload ? (
        <MarketDataAssetClassRequiredModal
          isOpen={instrumentResolveModals.assetClassOpen}
          onClose={instrumentResolveModals.onAssetClassClose}
          payload={instrumentResolveModals.assetClassPayload}
          onConfirm={instrumentResolveModals.onAssetClassConfirm}
        />
      ) : null}
      <MarketDataQuoteVariantModal
        isOpen={instrumentResolveModals.quoteVariantOpen}
        onClose={instrumentResolveModals.onQuoteVariantClose}
        tradeSymbol={instrumentResolveModals.quoteVariantTradeSymbol}
        candidates={instrumentResolveModals.quoteVariantCandidates}
        suggested={instrumentResolveModals.quoteVariantSuggested}
        onConfirm={instrumentResolveModals.onQuoteVariantConfirm}
        saving={instrumentResolveModals.savingQuoteVariant}
      />

      <Dialog
        open={editStrategyModal.isOpen}
        onOpenChange={(next) => {
          if (!next) editStrategyModal.onClose();
        }}
      >
        <DialogContent className="sm:max-w-lg" data-workspace-edit-strategy-dialog="">
          <DialogHeader>
            <DialogTitle>Edit strategy</DialogTitle>
          </DialogHeader>
          <FieldGroup className="gap-4">
            <Field>
              <FieldLabel htmlFor="workspace-edit-strategy-name">Name</FieldLabel>
              <Input
                id="workspace-edit-strategy-name"
                value={editStrategyModal.name}
                onChange={(event) => editStrategyModal.onNameChange(event.target.value)}
                maxLength={editStrategyModal.nameLimit}
              />
              <p className="font-[family-name:var(--font-primary)] text-xs text-muted-foreground">
                {editStrategyModal.name.length}/{editStrategyModal.nameLimit}
              </p>
            </Field>
            <Field>
              <FieldLabel htmlFor="workspace-edit-strategy-description">Description</FieldLabel>
              <Textarea
                id="workspace-edit-strategy-description"
                value={editStrategyModal.description}
                onChange={(event) => editStrategyModal.onDescriptionChange(event.target.value)}
                className="min-h-[140px]"
                maxLength={editStrategyModal.descriptionLimit}
              />
              <p className="font-[family-name:var(--font-primary)] text-xs text-muted-foreground">
                {editStrategyModal.description.length}/{editStrategyModal.descriptionLimit}
              </p>
            </Field>
            <label className="flex items-center gap-2 font-[family-name:var(--font-primary)] text-sm">
              <Checkbox
                checked={editStrategyModal.isArchived}
                onCheckedChange={(checked) =>
                  editStrategyModal.onArchivedChange(checked === true)
                }
              />
              Archive this strategy
            </label>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={editStrategyModal.onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              disabled={editStrategyModal.saving}
              onClick={() => void editStrategyModal.onSave()}
            >
              {editStrategyModal.saving ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
